
import React from 'react';
import { Button } from '@/components/ui/button';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { useToast } from '@/hooks/use-toast';
import { Bell } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type NotificationPermissionButtonProps = {
  size?: 'default' | 'sm' | 'lg' | 'icon';
  variant?: 'default' | 'outline' | 'secondary' | 'ghost' | 'link' | 'destructive';
};

const NotificationPermissionButton = ({ 
  size = 'default', 
  variant = 'outline' 
}: NotificationPermissionButtonProps) => {
  const { t } = useTranslation();
  const { isSupported, permissionStatus, requestPermission, isGranted } = usePushNotifications();
  const { toast } = useToast();

  const handleRequestPermission = async () => {
    try {
      const granted = await requestPermission();
      if (granted) {
        toast({
          title: t('pushNotifications.enabled'),
          description: t('pushNotifications.enabledDesc'),
        });
      } else {
        toast({
          title: t('pushNotifications.denied'),
          description: t('pushNotifications.deniedDesc'),
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      toast({
        title: t('pushNotifications.enableError'),
        description: "There was a problem enabling notifications",
        variant: "destructive",
      });
    }
  };

  if (!isSupported) {
    return null;
  }

  return (
    <Button 
      onClick={handleRequestPermission}
      variant={variant}
      size={size}
      disabled={isGranted || permissionStatus === 'denied'}
    >
      <Bell className="h-4 w-4 mr-2" />
      {isGranted 
        ? t('pushNotifications.enabled')
        : permissionStatus === 'denied'
          ? t('pushNotifications.blocked')
          : t('pushNotifications.enable')}
    </Button>
  );
};

export default NotificationPermissionButton;
